/**
 * Прокси конторы: откуда открывать эту кассу.
 *
 * Это доступ, не паспорт. Два IP в одном Chrome не делают второй отпечаток.
 * Строка живёт в счёте как человек её вставил: логин и пароль не вырезаем.
 * На сервер она едет в профиле того же человека.
 *
 * SOCKS5 с паролем Chrome в PAC не передаёт (handshake ≠ HTTP 407). Строку
 * принимаем, в PAC кладём хост:порт. HTTP/HTTPS CONNECT учётку умеет.
 */

import { knownDoors, parseDoor } from './hosts';

export type RoadScheme = 'http' | 'https' | 'socks5';

export interface RoadSecret {
  scheme: RoadScheme;
  host: string;
  port: number;
  user: string;
  pass: string;
}

export type ParseRoadFail = 'empty' | 'bad';

export type ParseRoad = { ok: true; secret: RoadSecret } | { ok: false; reason: ParseRoadFail };

export type RoadSecrets = Record<string, RoadSecret>;

const HOST_OK =
  /^(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+)|(?:\d{1,3}(?:\.\d{1,3}){3}))$/i;

function looksHost(host: string): boolean {
  return HOST_OK.test(host);
}

function portOf(raw: string): number | null {
  if (!/^\d{1,5}$/.test(raw)) return null;
  const port = Number(raw);
  if (port < 1 || port > 65535) return null;
  return port;
}

function schemeOf(raw: string): RoadScheme | null {
  const one = raw.toLowerCase();
  if (one === 'http' || one === 'https' || one === 'socks5') return one;
  if (one === 'socks') return 'socks5';
  return null;
}

function finish(scheme: RoadScheme, host: string, port: number, user: string, pass: string): ParseRoad {
  if (!looksHost(host)) return { ok: false, reason: 'bad' };
  return { ok: true, secret: { scheme, host: host.toLowerCase(), port, user, pass } };
}

/** host:port, user:pass@host:port, host:port@user:pass или host:port:user:pass. */
function parseHostPortUserPass(raw: string, scheme: RoadScheme): ParseRoad {
  const at = raw.lastIndexOf('@');
  if (at >= 0) {
    const left = raw.slice(0, at);
    const right = raw.slice(at + 1);
    const leftParts = left.split(':');
    const rightParts = right.split(':');
    if (rightParts.length === 2) {
      const port = portOf(rightParts[1] ?? '');
      if (port !== null) {
        const colon = left.indexOf(':');
        const user = colon < 0 ? left : left.slice(0, colon);
        const pass = colon < 0 ? '' : left.slice(colon + 1);
        return finish(scheme, rightParts[0] ?? '', port, user, pass);
      }
    }
    if (leftParts.length === 2) {
      const port = portOf(leftParts[1] ?? '');
      if (port !== null && right !== '') {
        const colon = right.indexOf(':');
        const user = colon < 0 ? right : right.slice(0, colon);
        const pass = colon < 0 ? '' : right.slice(colon + 1);
        return finish(scheme, leftParts[0] ?? '', port, user, pass);
      }
    }
    return { ok: false, reason: 'bad' };
  }

  const parts = raw.split(':');
  if (parts.length === 2) {
    const port = portOf(parts[1] ?? '');
    if (port === null) return { ok: false, reason: 'bad' };
    return finish(scheme, parts[0] ?? '', port, '', '');
  }
  if (parts.length >= 4) {
    const port = portOf(parts[1] ?? '');
    if (port === null) return { ok: false, reason: 'bad' };
    return finish(scheme, parts[0] ?? '', port, parts[2] ?? '', parts.slice(3).join(':'));
  }
  return { ok: false, reason: 'bad' };
}

/**
 * Разобрать строку, которую человек вставил. Пусто — прокси нет.
 * HTTP и SOCKS. Четыре поля без схемы — HTTP: так присылают пулы.
 */
export function parseRoadLine(raw: string | null | undefined): ParseRoad {
  if (typeof raw !== 'string') return { ok: false, reason: 'empty' };
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };
  if (/[\n\r]/.test(trimmed)) return { ok: false, reason: 'bad' };

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) {
    const cut = trimmed.indexOf('://');
    const scheme = schemeOf(trimmed.slice(0, cut));
    const rest = trimmed.slice(cut + 3);
    if (scheme === null) return { ok: false, reason: 'bad' };
    try {
      const url = new URL(trimmed);
      const port = url.port !== '' ? portOf(url.port) : scheme === 'https' ? 443 : scheme === 'http' ? 80 : null;
      if (port !== null && url.hostname !== '') {
        const got = finish(
          scheme,
          url.hostname,
          port,
          decodeURIComponent(url.username),
          decodeURIComponent(url.password),
        );
        if (got.ok) return got;
      }
    } catch {
      // socks5://host:port:user:pass — URL четвёртое поле не ест.
    }
    return parseHostPortUserPass(rest, scheme);
  }

  const head = trimmed.split(':')[0] ?? '';
  const named = schemeOf(head);
  if (named !== null) {
    return parseHostPortUserPass(trimmed.slice(head.length + 1), named);
  }

  return parseHostPortUserPass(trimmed, 'http');
}

export function parseFailText(reason: ParseRoadFail): string {
  if (reason === 'empty') return 'строка пустая';
  return 'строка не разобралась';
}

/** Хост без учётки — для PAC и подписи «через». Поле ввода это не подменяет. */
export function publicLine(secret: RoadSecret): string {
  return `${secret.scheme}://${secret.host}:${secret.port}`;
}

/** Что кладём в PAC. Без учётки и без `; DIRECT` — отказ не чинит бан домом. */
export function pacToken(secret: Pick<RoadSecret, 'scheme' | 'host' | 'port'>): string {
  const host = secret.host;
  const port = secret.port;
  if (secret.scheme === 'https') return `HTTPS ${host}:${port}`;
  if (secret.scheme === 'socks5') return `SOCKS5 ${host}:${port}`;
  return `PROXY ${host}:${port}`;
}

export const ALWAYS_DIRECT = [
  'forq.ru',
  'www.forq.ru',
  'localhost',
  '127.0.0.1',
  'local.adspower.net',
] as const;

/** Хосты, по которым меряем страну выхода. Все едут тем же PAC, иначе fallback светит дом. */
export const COUNTRY_CHECKS = [
  { host: 'ifconfig.co', url: 'https://ifconfig.co/json' },
  { host: 'ipinfo.io', url: 'https://ipinfo.io/json' },
  { host: 'api.ipify.org', url: 'https://api.ipify.org?format=json' },
  { host: 'ipapi.co', url: 'https://ipapi.co/json/' },
] as const;

export interface PacRoute {
  host: string;
  token: string;
}

function safeHost(host: string): boolean {
  return /^[a-z0-9.-]+$/.test(host) && host.length > 0 && !host.includes('..');
}

function matchLine(host: string, token: string): string {
  const lit = JSON.stringify(host);
  const ret = JSON.stringify(token);
  return `  if (host === ${lit} || dnsDomainIs(host, '.' + ${lit})) return ${ret};`;
}

/**
 * Скрипт PAC: маршрут по хосту, не по вкладке. Хост без прокси — DIRECT.
 * forq.ru и localhost всегда прямо: иначе сканер и стол уедут в чужой пул.
 */
export function buildPacScript(routes: readonly PacRoute[]): string {
  const seen = new Set<string>();
  const lines: string[] = ['function FindProxyForURL(url, host) {', '  host = host.toLowerCase();'];
  for (const host of ALWAYS_DIRECT) {
    lines.push(matchLine(host, 'DIRECT'));
    seen.add(host);
  }
  for (const one of routes) {
    const host = one.host.toLowerCase();
    if (!safeHost(host) || seen.has(host)) continue;
    if (ALWAYS_DIRECT.some((lock) => host === lock || host.endsWith(`.${lock}`))) continue;
    const token = one.token === 'DIRECT' ? 'DIRECT' : one.token;
    if (token !== 'DIRECT' && /;\s*DIRECT\s*$/i.test(token)) continue;
    lines.push(matchLine(host, token));
    seen.add(host);
  }
  lines.push("  return 'DIRECT';", '}');
  return lines.join('\n');
}

/**
 * Зеркала, которых нет в каноне HOSTS: PAC обязан их крыть, полоса на них
 * не обязана. Иначе клик открывает ggbet.ru, а PAC знает только gg.bet.
 */
const PAC_EXTRA: Record<string, readonly string[]> = {
  ggbet: ['ggbet.com', 'ggbet.ru'],
  thunderpick: ['thunderpick.com', 'thunderpick.bet'],
};

/** Хосты этой конторы и её двери — на её прокси. Остальные DIRECT. */
export function routesOf(doors: Record<string, string>, secrets: RoadSecrets): PacRoute[] {
  const routes: PacRoute[] = [];
  const put = (host: string, token: string): void => {
    routes.push({ host, token });
  };
  for (const [venue, secret] of Object.entries(secrets)) {
    const token = pacToken(secret);
    for (const host of knownDoors(venue)) put(host, token);
    for (const host of PAC_EXTRA[venue] ?? []) put(host, token);
    const door = parseDoor(doors[venue] ?? '');
    if (door !== null) put(door.host, token);
  }
  return routes;
}

export function hasProxy(secrets: RoadSecrets): boolean {
  return Object.keys(secrets).length > 0;
}

export interface ProbeClass {
  fail: string;
  dead: boolean;
}

/**
 * Класс ответа пробы. Туннель жив, если прокси ответил чем угодно, кроме 407.
 * 403/451 кассы — не мёртвая дорога: HTML режет Cloudflare, выход при этом есть.
 */
export function classifyProbe(status: number, error: string, exit = ''): ProbeClass {
  if (status === 407) return { fail: 'не открылось', dead: true };
  if (exit !== '') return { fail: '', dead: false };
  if (error !== '' || status === 0) return { fail: 'не открылось', dead: true };
  return { fail: '', dead: false };
}

export function parseExit(body: unknown): { country: string; ip: string } {
  if (typeof body !== 'object' || body === null) return { country: '', ip: '' };
  const box = body as Record<string, unknown>;
  let country = '';
  for (const key of ['country_iso', 'country_code', 'countryCode', 'country', 'country_name']) {
    const raw = box[key];
    if (typeof raw === 'string' && raw.trim() !== '') {
      country = raw.trim();
      break;
    }
  }
  const ipRaw = box.ip ?? box.query;
  const ip = typeof ipRaw === 'string' ? ipRaw.trim() : '';
  return { country: countryIso(country), ip };
}

/** Ответ Cloudflare `/cdn-cgi/trace`: `ip=…` и `loc=XX` — выход, как его видит контора. */
export function parseTrace(text: string): { country: string; ip: string } {
  const field = (name: string): string => new RegExp(`^${name}=(.*)$`, 'm').exec(text)?.[1]?.trim() ?? '';
  const loc = field('loc');
  return { country: loc === '' || loc === 'XX' ? '' : countryIso(loc), ip: field('ip') };
}

export function parseCountry(body: unknown): string {
  return parseExit(body).country;
}

const COUNTRY_NAME: Record<string, string> = {
  AT: 'Австрия',
  CH: 'Швейцария',
  CZ: 'Чехия',
  DE: 'Германия',
  ES: 'Испания',
  FI: 'Финляндия',
  FR: 'Франция',
  GB: 'Британия',
  IE: 'Ирландия',
  IT: 'Италия',
  NL: 'Нидерланды',
  PL: 'Польша',
  PT: 'Португалия',
  RO: 'Румыния',
  SE: 'Швеция',
  TR: 'Турция',
  UA: 'Украина',
  US: 'США',
};

const COUNTRY_ALIAS: Record<string, string> = {
  switzerland: 'CH',
  germany: 'DE',
  netherlands: 'NL',
  france: 'FR',
  austria: 'AT',
  poland: 'PL',
  portugal: 'PT',
  spain: 'ES',
  italy: 'IT',
  sweden: 'SE',
  finland: 'FI',
  ireland: 'IE',
  romania: 'RO',
  turkey: 'TR',
  ukraine: 'UA',
  'united states': 'US',
  'united kingdom': 'GB',
  'great britain': 'GB',
  czechia: 'CZ',
  'czech republic': 'CZ',
};

/** ISO, если узнали. Иначе как пришло — IP сюда не кладём. */
export function countryIso(raw: string): string {
  const one = raw.trim();
  if (one === '') return '';
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(one)) return '';
  if (/^[A-Za-z]{2}$/.test(one)) return one.toUpperCase();
  return COUNTRY_ALIAS[one.toLowerCase()] ?? one;
}

export function countryLabel(raw: string): string {
  const iso = countryIso(raw);
  if (iso.length === 2) return COUNTRY_NAME[iso] ?? iso;
  return iso;
}
